// Frontend logging utility with sensitive data sanitization
class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';
    
    // Sensitive data patterns to sanitize
    this.sensitivePatterns = [
      // JWT tokens
      /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g,
      // Phone numbers
      /(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})/g,
      /(\+?[1-9]\d{1,14})/g,
      // Email addresses
      /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
      // Passwords
      /"(password|pwd|pass|secret|token|key|auth)"\s*:\s*"[^"]*"/gi,
      // Credit card numbers
      /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
      // SSN
      /\b\d{3}-\d{2}-\d{4}\b/g,
      // Session IDs
      /session[_-]?id\s*[:=]\s*[A-Za-z0-9+/=]+/gi
    ];
  }

  // Sanitize sensitive data
  sanitizeData(data) {
    if (typeof data === 'string') {
      let sanitized = data;
      this.sensitivePatterns.forEach(pattern => {
        sanitized = sanitized.replace(pattern, '[REDACTED]');
      });
      return sanitized;
    }
    
    if (typeof data === 'object' && data !== null) {
      if (Array.isArray(data)) {
        return data.map(item => this.sanitizeData(item));
      }
      
      const sanitized = {};
      for (const [key, value] of Object.entries(data)) {
        // Check if key indicates sensitive data
        const sensitiveKeys = ['password', 'pwd', 'pass', 'secret', 'token', 'key', 'auth', 'phone', 'email', 'ssn', 'creditCard'];
        const isSensitiveKey = sensitiveKeys.some(sensitiveKey => 
          key.toLowerCase().includes(sensitiveKey)
        );
        
        if (isSensitiveKey) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = this.sanitizeData(value);
        }
      }
      return sanitized;
    }
    
    return data;
  }

  // Format log message
  formatMessage(level, message, data) {
    const timestamp = new Date().toISOString();
    const sanitizedData = data ? this.sanitizeData(data) : null;
    
    return {
      timestamp,
      level,
      message: this.sanitizeData(message),
      data: sanitizedData
    };
  }

  // Log methods
  debug(message, data) {
    if (this.isDevelopment) {
      const logData = this.formatMessage('DEBUG', message, data);
      console.debug(`[${logData.timestamp}] DEBUG: ${logData.message}`, logData.data);
    }
  }

  info(message, data) {
    if (this.isDevelopment) {
      const logData = this.formatMessage('INFO', message, data);
      console.info(`[${logData.timestamp}] INFO: ${logData.message}`, logData.data);
    }
  }

  warn(message, data) {
    const logData = this.formatMessage('WARN', message, data);
    if (this.isDevelopment) {
      console.warn(`[${logData.timestamp}] WARN: ${logData.message}`, logData.data);
    }
    // In production, you might want to send warnings to a logging service
  }

  error(message, data) {
    const logData = this.formatMessage('ERROR', message, data);
    if (this.isDevelopment) {
      console.error(`[${logData.timestamp}] ERROR: ${logData.message}`, logData.data);
    }
    // In production, you might want to send errors to a logging service
  }

  // Security logging for authentication events
  authEvent(event, data) {
    const logData = this.formatMessage('AUTH', event, data);
    if (this.isDevelopment) {
      console.info(`[${logData.timestamp}] AUTH: ${logData.message}`, logData.data);
    }
  }

  // Security logging for suspicious activities
  securityEvent(event, data) {
    const logData = this.formatMessage('SECURITY', event, data);
    if (this.isDevelopment) {
      console.warn(`[${logData.timestamp}] SECURITY: ${logData.message}`, logData.data);
    }
  }

  // API request logging
  apiRequest(method, url, data) {
    const logData = this.formatMessage('API_REQUEST', `${method} ${url}`, data);
    if (this.isDevelopment) {
      console.info(`[${logData.timestamp}] API: ${logData.message}`, logData.data);
    }
  }

  // API response logging
  apiResponse(method, url, status, data) {
    const logData = this.formatMessage('API_RESPONSE', `${method} ${url} - ${status}`, data);
    if (this.isDevelopment) {
      console.info(`[${logData.timestamp}] API: ${logData.message}`, logData.data);
    }
  }
}

// Create singleton instance
const logger = new Logger();

// Export both the instance and the class
export default logger;
export { Logger };
